/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Api } from "../../services/interceptor";
import { getAuthToken } from "../../helper/helper";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { selectSelectedLeague, setSelectedLeague } from "../../redux/slice/selectedLeagueSlice";
import { fetchAllLeagues, mapLeagueItemToGetAllLeagues } from "../myLeagues/api/myLeaguesApi";
import { buildMinimalLeagueFromWebSearch } from "../../utils/websearchParse";
import UpdateTeamModal from "../leaguesHome/teams/teamInfo/UpdateTeamModal";
import { useTournamentMatches } from "../tournamentPage/useTournamentMatches";
import { buildAllVms, codeFor, colorFor, startsIn, type MatchVm } from "../tournamentPage/matchVm";
import { FormPills, TeamCrest } from "../teamsTab/TeamsTab";
import { norm, recordFromMatches, teamPath, teamsTabPath, unwrap, useTeamLeaderboard, useTeamRecords, type BatLine, type BowlLine, type TeamRecord } from "../teamsTab/teamsData";
import SquadTab from "./SquadTab";
import { useRoster } from "./rosterData";
import TeamMatchesTab from "./TeamMatchesTab";
import TeamStatsTab from "./TeamStatsTab";
import PhotosTab, { usePhotos } from "./PhotosTab";
import "../tournamentPage/tournamentPage.scss";
import "../teamsTab/teamsTab.scss";
import "./teamPage.scss";

export type TeamCtx = {
  tid: string;
  ts: string;
  teamId: string;
  leagueId: string;
  name: string;
  code: string;
  color: string;
  image?: string;
  canEdit: boolean;
  captainId?: string | null;
  viceId?: string | null;
  wkId?: string | null;
  tournamentName: string;
  tournament: any;
  players: number | null;
  matches: MatchVm[];
  matchesLoading: boolean;
  batting: BatLine[];
  bowling: BowlLine[];
  statsLoading: boolean;
  openEdit: () => void;
};

const TABS = [["squad", "Squad"], ["matches", "Matches"], ["stats", "Stats"], ["photos", "Photos"]] as const;
type TabKey = (typeof TABS)[number][0];

const Back = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const Share = () => <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><circle cx="12" cy="3.5" r="2" stroke="currentColor" strokeWidth="1.6" /><circle cx="4" cy="8" r="2" stroke="currentColor" strokeWidth="1.6" /><circle cx="12" cy="12.5" r="2" stroke="currentColor" strokeWidth="1.6" /><path d="M5.8 7l4.4-2.5M5.8 9l4.4 2.5" stroke="currentColor" strokeWidth="1.6" /></svg>;

const TeamPage = () => {
  const { tournamentId: urlTid = "", teamSnapshotId: ts = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const tabParam = sp.get("tab");
  const tab: TabKey = TABS.some(([k]) => k === tabParam) ? (tabParam as TabKey) : "squad";

  const detailsQ = useQuery({
    queryKey: ["tgDetails", ts],
    enabled: !!ts,
    retry: false,
    queryFn: async () => unwrap<any>((await Api.get(`teamSnapshot/details/${ts}`)).data),
  });
  const d = detailsQ.data;
  const tid: string = d?.tournament?.tournamentId ?? urlTid;
  const leagueId: string = d?.leagueId ?? "";
  const name: string = d?.teamName ?? "";
  const tournamentName: string = d?.tournament?.tournamentName ?? "";

  // The URL is the only context a shared link has: make the rest of the app (header, league permissions, edit modal) see this tournament too.
  useEffect(() => {
    if (!d) return;
    if (tid) { sessionStorage.setItem("tournamentId", tid); sessionStorage.setItem("tournamentName", tournamentName); }
    if (leagueId) sessionStorage.setItem("leagueId", leagueId);
  }, [d, tid, tournamentName, leagueId]);
  // a link with a stale / placeholder tournament id is corrected to the team's real one, so what gets copied next is right
  useEffect(() => {
    const real = d?.tournament?.tournamentId;
    if (real && urlTid && real !== urlTid) navigate(teamPath(real, ts, tabParam ?? undefined), { replace: true });
  }, [d, urlTid, ts, tabParam, navigate]);
  const needsLeague = !!leagueId && selectedLeague?.leagueId !== leagueId;
  const leaguesQ = useQuery({ queryKey: ["myLeagues", "allLeague"], queryFn: fetchAllLeagues, enabled: needsLeague && !!getAuthToken(), staleTime: 30_000, retry: false });
  useEffect(() => {
    if (!needsLeague) return;
    const match = leaguesQ.data?.find((l) => l.leagueId === leagueId);
    if (match) dispatch(setSelectedLeague(mapLeagueItemToGetAllLeagues(match)));
    else if (leaguesQ.isFetched || leaguesQ.isError || !getAuthToken()) dispatch(setSelectedLeague(buildMinimalLeagueFromWebSearch(leagueId, d?.tournament?.leagueName ?? "")));
  }, [needsLeague, leaguesQ.data, leaguesQ.isFetched, leaguesQ.isError, leagueId, dispatch, d]);

  const all = useTournamentMatches(tid || null);
  const vms = useMemo(() => buildAllVms(all.live, all.up, all.past), [all.live, all.up, all.past]);
  const mine = useMemo(
    () => vms.filter((m) => [m.raw?.teamOne, m.raw?.teamTwo].some((t: any) => (t?.teamSnapshotId && t.teamSnapshotId === ts) || (!t?.teamSnapshotId && norm(t?.teamName) === norm(name)))),
    [vms, ts, name],
  );
  const records = useTeamRecords(d?.division?.divisionId ? [d.division.divisionId] : []);
  // from the finished matches (so it matches the Matches tab); the points table covers the moments before they load
  const rec: TeamRecord | undefined = recordFromMatches(vms, ts, name) ?? records.byId.get(ts) ?? records.byName.get(norm(name));
  const lb = useTeamLeaderboard(tid || undefined, ts, !!tid);
  const photos = usePhotos(ts);
  const roster = useRoster(ts).data ?? [];
  const nameOf = (id?: string | null) => (id ? roster.find((p) => p.id === id)?.name ?? "" : "");
  const [editOpen, setEditOpen] = useState(false);

  const code = codeFor(name, d?.teamCode);
  const color = colorFor(name);
  const ctx: TeamCtx = {
    tid, ts, teamId: d?.teamId ?? "", leagueId, name, code, color, image: d?.imageUrl || undefined,
    canEdit: !!d?.canEditTeam, captainId: d?.captainPlayerId, viceId: d?.viceCaptainPlayerId, wkId: d?.wkPlayerId,
    tournamentName, tournament: d?.tournament, players: typeof d?.totalPlayers === "number" ? d.totalPlayers : null,
    matches: mine, matchesLoading: all.loading, batting: lb.batting, bowling: lb.bowling, statsLoading: lb.loading,
    openEdit: () => setEditOpen(true),
  };

  useEffect(() => {
    if (name) document.title = `${name} · Teams · ${tournamentName || "Tournament"} · CricCenter`;
    return () => { document.title = "CricCenter"; };
  }, [name, tournamentName]);

  // sticky bar shows the team once the big title scrolls away
  const idRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = idRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), { rootMargin: "-90px 0px 0px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [d]);

  const setTab = (k: TabKey) => {
    const next = new URLSearchParams(sp);
    next.set("tab", k);
    setSp(next, { replace: true });
    const bar = document.getElementById("tg-tabsbar");
    if (bar && window.scrollY > bar.offsetTop) window.scrollTo({ top: Math.max(0, bar.offsetTop - 120), behavior: "smooth" });
  };

  const share = async () => {
    const url = `${window.location.origin}${teamPath(tid, ts, tab === "squad" ? undefined : tab)}`;
    try {
      if (navigator.share) await navigator.share({ title: `${name} · ${tournamentName}`, url });
      else { await navigator.clipboard.writeText(url); toast.success("Team link copied"); }
    } catch { /* cancelled */ }
  };

  if (detailsQ.isLoading) {
    return (
      <div className="tp tp-hero tg"><section className="hero thero"><div className="hbg" aria-hidden="true"><span className="halo" /><span className="tile" /><span className="scrim-l" /><span className="scrim-b" /><span className="grain" /></div><div className="wrap" style={{ paddingTop: 18, minHeight: 280 }}><div className="tg-loading">Loading team…</div></div></section></div>
    );
  }
  if (detailsQ.isError || !d) {
    return (
      <div className="tp tg"><main className="wrap main"><div className="empty"><h3>We couldn’t find this team</h3><p>The link may be old, or the team was removed from the tournament.</p><div className="acts"><button className="btn primary" onClick={() => navigate(urlTid ? teamsTabPath(urlTid) : "/")}>{urlTid ? "Back to teams" : "Go home"}</button></div></div></main></div>
    );
  }

  // ---------- hero facts ----------
  const topBat = [...lb.batting].sort((a, b) => b.runs - a.runs)[0];
  const topBowl = [...lb.bowling].sort((a, b) => b.wkts - a.wkts || a.runs - b.runs)[0];
  const next = mine.filter((m) => m.bucket === "up").sort((a, b) => a.ms - b.ms)[0];
  const opp = (m: MatchVm) => (norm(m.a.name) === norm(name) ? m.b.name : m.a.name);
  const played = rec?.played ?? mine.filter((m) => m.bucket === "past").length;
  const divName = [d.division?.divisionName, d.group?.divisionName].filter(Boolean).join(" · ");
  const liveNow = mine.some((m) => m.bucket === "live");
  const none = <small>—</small>;
  const captainName = nameOf(d.captainPlayerId);
  const viceName = nameOf(d.viceCaptainPlayerId);

  const facts = played > 0 && rec ? (
    <>
      <div><div className="k">Played</div><div className="v">{played} {played === 1 ? "match" : "matches"}</div></div>
      <div><div className="k">Won · Lost · Tied</div><div className="v"><span className="n">{rec.won}</span> · <span className="n">{rec.lost}</span> · <span className="n">{rec.tied}</span>{rec.nr > 0 && <small> · {rec.nr} no result</small>}</div></div>
      <div><div className="k">Last {Math.min(5, rec.form.length || 5)}</div><div className="v">{rec.form.length ? <FormPills form={rec.form} big /> : none}</div></div>
      <div><div className="k">Top run-scorer</div><div className="v">{topBat && topBat.runs > 0 ? <>{topBat.name} <small>· {topBat.runs} runs</small></> : none}</div></div>
      <div><div className="k">Top wicket-taker</div><div className="v">{topBowl && topBowl.wkts > 0 ? <>{topBowl.name} <small>· {topBowl.wkts} wkts</small></> : none}</div></div>
    </>
  ) : (
    <>
      <div><div className="k">Played</div><div className="v">Yet to play</div></div>
      <div><div className="k">Next match</div><div className="v">{next ? <>v {opp(next)} <small>· {startsIn(next.start).when}, {next.time}</small></> : <small>Fixtures not out yet</small>}</div></div>
      <div><div className="k">Squad</div><div className="v">{ctx.players == null ? none : `${ctx.players} ${ctx.players === 1 ? "player" : "players"}`}</div></div>
      {captainName && <div><div className="k">Captain</div><div className="v">{captainName}</div></div>}
      <div><div className="k">Division</div><div className="v">{divName || "—"}</div></div>
    </>
  );

  const counts: Record<TabKey, number | null> = { squad: ctx.players, matches: mine.length, stats: null, photos: photos.items.length };

  return (
    <>
      <div className="tp tp-hero tg">
        <section className="hero thero">
          <div className="hbg" aria-hidden="true"><span className="halo" /><span className="tile" /><span className="scrim-l" /><span className="scrim-b" /><span className="grain" /></div>
          <div className="wrap" style={{ paddingTop: 18 }}>
            <div className="tg-top">
              <Link className="backl" to={teamsTabPath(tid)}><Back />All teams</Link>
              <nav className="crumb" aria-label="Breadcrumb"><Link to={`/tournamentDetails/${tid}`}>{tournamentName || "Tournament"}</Link><span>/</span><Link to={teamsTabPath(tid)}>Teams</Link><span>/</span><b>{name}</b></nav>
            </div>
            <div className="hgrid">
              <div className="tid" ref={idRef}>
                <TeamCrest t={{ name, code, color, image: ctx.image }} size={84} />
                <div className="tid-tx">
                  <div className="tid-badges">
                    {divName && <span className="badge">{divName.toUpperCase()}</span>}
                    {liveNow && <span className="badge live"><span className="ldot on" />LIVE NOW</span>}
                  </div>
                  <h1 style={name.length > 30 ? { fontSize: 30 } : undefined}>{name}</h1>
                  {(captainName || viceName || ctx.players != null) && <div className="tid-sub">{[captainName && <>Captain <b>{captainName}</b></>, viceName && <>Vice-captain <b>{viceName}</b></>, ctx.players != null && <>{ctx.players} {ctx.players === 1 ? "player" : "players"}</>].filter(Boolean).map((x, i) => <span key={i}>{i > 0 && " · "}{x}</span>)}</div>}
                </div>
              </div>
              <div className="hacts">
                {ctx.canEdit && <button className="hbtn" onClick={() => { sessionStorage.setItem("tournamentId", tid); setEditOpen(true); }}>Edit team</button>}
                <button className="hbtn solid" onClick={() => void share()}><Share />Share</button>
              </div>
            </div>
            <div className="facts tg-facts">{facts}</div>
          </div>
        </section>
      </div>

      <div className="tp tp-sticky tg">
        <div className="tabsbar" id="tg-tabsbar">
          <div className="wrap">
            <button className={`mini ${stuck ? "on" : ""}`} tabIndex={stuck ? 0 : -1} aria-hidden={!stuck} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
              <TeamCrest t={{ name, code, color, image: ctx.image }} size={26} />{name}
            </button>
            <div className="tabs ptabs" role="tablist" aria-label="Team sections">
              {TABS.map(([k, l]) => (
                <button key={k} className="tab" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{l}{counts[k] != null && <span className="tct">{counts[k]}</span>}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="tp tg">
        <main className="wrap main">
          {tab === "squad" && <SquadTab ctx={ctx} />}
          {tab === "matches" && <TeamMatchesTab ctx={ctx} />}
          {tab === "stats" && <TeamStatsTab ctx={ctx} />}
          {tab === "photos" && <PhotosTab ctx={ctx} />}
        </main>
      </div>

      <UpdateTeamModal
        open={editOpen}
        onOpenChange={setEditOpen}
        teamId={ctx.teamId}
        teamSnapshotIdFromList={ts}
        initialData={{ teamName: name }}
        onSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: ["tgDetails", ts] });
          void queryClient.invalidateQueries({ queryKey: ["tmTeams", tid] });
          void queryClient.invalidateQueries({ queryKey: ["tgRoster", ts] });
        }}
      />
    </>
  );
};

export default TeamPage;
